import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Clock, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PendingOpportunitiesPanel() {
  const [items, setItems] = useState([]);
  const [retrying, setRetrying] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pending-opportunities`, { withCredentials: true });
      setItems(data?.pending || []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const retry = async (id) => {
    setRetrying((r) => ({ ...r, [id]: true }));
    try {
      const { data } = await axios.post(`${API}/pending-opportunities/${id}/retry`, {}, { withCredentials: true });
      if (data?.synced) {
        toast.success("Opportunità sincronizzata su GHL ✓");
      } else {
        toast.info("GHL ancora occupato — riproveremo automaticamente a breve");
      }
    } catch {
      toast.error("Errore durante il tentativo di sincronizzazione");
    } finally {
      setRetrying((r) => ({ ...r, [id]: false }));
      load();
    }
  };

  if (items.length === 0) return null;

  return (
    <div
      data-testid="pending-opportunities-panel"
      className="mb-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-amber-900">
          {items.length} opportunità in attesa di sincronizzazione su GHL
        </span>
      </div>
      <p className="text-xs text-amber-800/80 mb-3">
        Salvate nel backend perché GHL era momentaneamente occupato. Verranno sincronizzate automaticamente; puoi anche forzare un tentativo ora.
      </p>
      <div className="space-y-2">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`pending-item-${it.id}`}
            className="flex items-center justify-between gap-3 rounded-xl bg-white border border-amber-200 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate">
                {it.contact_name || "—"}
                {it.assicurazione_name ? <span className="text-slate-400"> · {it.assicurazione_name}</span> : null}
              </div>
              <div className="text-[11px] text-slate-400 tabular-nums">
                €{Math.round(it.total_cost || 0)} · tentativi: {it.attempts || 0}
                {it.has_photos ? " · foto da riaggiungere dopo il sync" : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => retry(it.id)}
              disabled={retrying[it.id]}
              data-testid={`pending-retry-${it.id}`}
              className="inline-flex items-center gap-1.5 shrink-0 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-1.5 transition-colors disabled:opacity-60"
            >
              {retrying[it.id] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Riprova ora
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
